import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { Loader2 } from 'lucide-react';
import {
  clearStoredLockUntil,
  extractLockedUntilIso,
  formatLockCountdownMmSs,
  readStoredLockUntilIso,
  remainingMsFromIso,
  writeStoredLockUntilIso,
} from '@/lib/loginLockout';
import { getShellAuthApiBaseUrl } from '@/services/apiClient';

const RESEND_COOLDOWN_SEC = 60;

type LocationState = { challengeId?: string; email?: string };

export default function LoginVerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyLoginOtp, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const state = (location.state || {}) as LocationState;
  const challengeId = state.challengeId;
  const email = state.email ?? '';

  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);
  const [lockDeadlineIso, setLockDeadlineIso] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const lockRemainingMs = useMemo(() => {
    if (!lockDeadlineIso) return 0;
    return remainingMsFromIso(lockDeadlineIso);
  }, [lockDeadlineIso, tick]);

  useEffect(() => {
    if (!email.trim()) {
      setLockDeadlineIso(null);
      return;
    }
    setLockDeadlineIso(readStoredLockUntilIso(email));
  }, [email]);

  useEffect(() => {
    if (!lockDeadlineIso) return;
    const emailNorm = email.trim();
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
      const rem = remainingMsFromIso(lockDeadlineIso);
      if (rem <= 0) {
        setLockDeadlineIso(null);
        if (emailNorm) clearStoredLockUntil(emailNorm);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [lockDeadlineIso, email]);

  const startCooldown = useCallback(() => {
    setCooldownSec(RESEND_COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    if (!challengeId) {
      navigate('/login', { replace: true });
      return;
    }
    startCooldown();
  }, [challengeId, navigate, startCooldown]);

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as LocationState & { from?: { pathname?: string } })?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = setInterval(() => {
      setCooldownSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownSec]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId) return;
    const code = otp.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) return;

    setIsSubmitting(true);
    try {
      await verifyLoginOtp(challengeId, code);
      if (email.trim()) clearStoredLockUntil(email.trim());
      setLockDeadlineIso(null);
      toast({
        title: 'Welcome back!',
        description: 'You have successfully logged in.',
      });
    } catch (error: unknown) {
      const err = error as Error & {
        statusCode?: number;
        lockedUntil?: string;
        authErrorPayload?: unknown;
      };
      // Subscription-expired short-circuit — redirect to the expiry landing
      // page. The backend returns 403 with { code: 'SUBSCRIPTION_EXPIRED', expiredOn }.
      const payload = err.authErrorPayload as { code?: string; expiredOn?: string | null } | undefined;
      if (err.statusCode === 403 && payload?.code === 'SUBSCRIPTION_EXPIRED') {
        navigate('/subscription-expired', {
          replace: true,
          state: { expiredOn: payload.expiredOn ?? null, email: email.trim() },
        });
        return;
      }
      const locked = err.statusCode === 429;
      const lockIso =
        err.lockedUntil ??
        extractLockedUntilIso(err.authErrorPayload) ??
        readStoredLockUntilIso(email.trim()) ??
        undefined;
      if (locked && lockIso && email.trim()) {
        writeStoredLockUntilIso(email.trim(), lockIso);
        setLockDeadlineIso(lockIso);
      }
      const remainingForMsg = lockIso ? remainingMsFromIso(lockIso) : 0;
      toast({
        title: locked ? 'Too many failed attempts' : 'Verification failed',
        description: locked
          ? remainingForMsg > 0
            ? `Too many failed attempts. Try again in ${formatLockCountdownMmSs(remainingForMsg)}`
            : 'Too many failed attempts. Please try again in a moment.'
          : err.message || 'Invalid or expired OTP',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!challengeId || cooldownSec > 0 || isResending) return;
    setIsResending(true);
    try {
      const res = await api.resendLoginOtp(challengeId);
      if (res.isSuccess) {
        startCooldown();
        toast({
          title: 'Code sent',
          description: 'Check your email for a new verification code.',
        });
      } else {
        const isCooldown = res.statusCode === 429;
        toast({
          title: 'Could not resend',
          description: isCooldown
            ? 'Please wait before requesting a new code.'
            : res.message || 'Invalid or expired OTP',
          variant: 'destructive',
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  if (!challengeId) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="DataCaffe Logo"
              className="h-16 w-16 object-contain"
            />
            <span className="text-2xl font-bold">
              <span style={{ color: '#f97316' }}>ESG</span>
              <span className="text-blue-600 dark:text-blue-400">Caffe</span>
            </span>
          </div>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Check your email</CardTitle>
            <CardDescription className="text-center">
              {email
                ? `Enter the 6-digit code we sent to ${email}. The code is valid for 5 minutes.`
                : 'Enter the 6-digit verification code from your email. The code is valid for 5 minutes.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {lockRemainingMs > 0 && (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100"
                  role="status"
                >
                  Too many failed attempts. Try again in {formatLockCountdownMmSs(lockRemainingMs)}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="otp">Verification code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  className="text-center text-lg tracking-widest"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={
                  isSubmitting || lockRemainingMs > 0 || otp.replace(/\D/g, '').length !== 6
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify and sign in'
                )}
              </Button>
              <div className="flex flex-col items-center gap-2 text-sm">
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-blue-600 dark:text-blue-400"
                  disabled={cooldownSec > 0 || isResending}
                  onClick={handleResend}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin inline" />
                      Sending...
                    </>
                  ) : cooldownSec > 0 ? (
                    `Resend code in ${cooldownSec}s`
                  ) : (
                    'Resend code'
                  )}
                </Button>
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Back to sign in
                </Link>
              </div>
              {import.meta.env.DEV && (
                <p
                  className="text-center text-xs text-muted-foreground pt-1"
                  title="Override with VITE_AUTH_API_URL or VITE_API_URL in .env"
                >
                  <span className="font-medium text-foreground/70">Auth API base</span>{' '}
                  <code className="rounded bg-muted/70 px-1 py-0.5 text-[11px] break-all">
                    {getShellAuthApiBaseUrl()}
                  </code>
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
